import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useWebSocket } from '@/context/WebSocketContext';
import { useLanguage } from '@/context/LanguageContext';
import { devicesAPI } from '@/lib/api';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { 
  ArrowLeft, Desktop, MapPin, Globe, Clock, PencilSimple, Trash, CircleNotch,
  Copy, Check, Info, Terminal, Play, Lightning, HardDrives, ArrowClockwise,
  Broom, Wrench, WifiHigh, ShieldCheck, FileText, CheckCircle, XCircle,
  CaretDown, CaretRight, FolderOpen, WarningCircle
} from '@phosphor-icons/react';

const scriptTemplates = [
  { id: 'sysinfo', name: 'scripts.sysinfo', description: 'Hostname, OS, RAM, CPU, Uptime', icon: Info, color: 'text-blue-400 bg-blue-500/10 border-blue-500/20', script_type: 'powershell', script_content: `$os = Get-CimInstance Win32_OperatingSystem\n$cpu = Get-CimInstance Win32_Processor | Select-Object -First 1\n$uptime = (Get-Date) - $os.LastBootUpTime\nWrite-Output "=== Systeminformationen ==="\nWrite-Output "Hostname: $($env:COMPUTERNAME)"\nWrite-Output "OS: $($os.Caption) $($os.Version)"\nWrite-Output "RAM: $([math]::Round($os.TotalVisibleMemorySize/1MB,1)) GB (Frei: $([math]::Round($os.FreePhysicalMemory/1MB,1)) GB)"\nWrite-Output "CPU: $($cpu.Name)"\nWrite-Output "Uptime: $($uptime.Days)d $($uptime.Hours)h $($uptime.Minutes)m"` },
  { id: 'diskspace', name: 'scripts.diskspace', description: 'Speicherplatz aller Laufwerke', icon: HardDrives, color: 'text-purple-400 bg-purple-500/10 border-purple-500/20', script_type: 'powershell', script_content: `Get-CimInstance Win32_LogicalDisk -Filter "DriveType=3" | ForEach-Object {\n  $total = [math]::Round($_.Size/1GB,1)\n  $free = [math]::Round($_.FreeSpace/1GB,1)\n  $used = $total - $free\n  $pct = [math]::Round(($used/$total)*100,0)\n  Write-Output "$($_.DeviceID) $used/$total GB belegt ($pct%)"\n}` },
  { id: 'winupdate', name: 'scripts.winupdate', description: 'Updates prüfen & installieren', icon: ArrowClockwise, color: 'text-green-400 bg-green-500/10 border-green-500/20', script_type: 'powershell', script_content: `Write-Output "Prüfe Windows Updates..."\nStart-Process -FilePath "UsoClient.exe" -ArgumentList "StartScan" -Wait\nStart-Process -FilePath "UsoClient.exe" -ArgumentList "StartDownload" -Wait\nStart-Process -FilePath "UsoClient.exe" -ArgumentList "StartInstall" -Wait\nWrite-Output "Windows Update Scan + Installation gestartet."` },
  { id: 'diskcleaner', name: 'scripts.diskcleaner', description: 'Temp-Dateien & Cache löschen', icon: Broom, color: 'text-orange-400 bg-orange-500/10 border-orange-500/20', script_type: 'powershell', script_content: `Write-Output "=== Disk Cleanup ==="\n$before = (Get-CimInstance Win32_LogicalDisk -Filter "DeviceID='C:'").FreeSpace\nRemove-Item "$env:TEMP\\*" -Recurse -Force -ErrorAction SilentlyContinue\nRemove-Item "C:\\Windows\\Temp\\*" -Recurse -Force -ErrorAction SilentlyContinue\n$after = (Get-CimInstance Win32_LogicalDisk -Filter "DeviceID='C:'").FreeSpace\n$freed = [math]::Round(($after - $before)/1MB, 1)\nWrite-Output "Bereinigt: $freed MB freigegeben"` },
  { id: 'services', name: 'scripts.services', description: 'Wichtige Windows-Dienste', icon: Wrench, color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20', script_type: 'powershell', script_content: `$services = @("WinRM","Spooler","W32Time","wuauserv","BITS","EventLog","Dnscache")\nWrite-Output "=== Dienste-Status ==="\nforeach ($svc in $services) {\n  $s = Get-Service -Name $svc -ErrorAction SilentlyContinue\n  if ($s) { Write-Output "$($s.DisplayName): $($s.Status)" }\n  else { Write-Output "$svc: NICHT GEFUNDEN" }\n}` },
  { id: 'network', name: 'scripts.network', description: 'IP, DNS und Gateway', icon: WifiHigh, color: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20', script_type: 'powershell', script_content: `Write-Output "=== Netzwerk ==="\nGet-NetIPAddress -AddressFamily IPv4 | Where-Object { $_.InterfaceAlias -notlike "Loopback*" } | ForEach-Object {\n  Write-Output "$($_.InterfaceAlias): $($_.IPAddress)/$($_.PrefixLength)"\n}\n$gw = (Get-NetRoute -DestinationPrefix "0.0.0.0/0" -ErrorAction SilentlyContinue | Select-Object -First 1).NextHop\nWrite-Output "Gateway: $gw"` },
  { id: 'restart', name: 'scripts.restart', description: 'Neustart in 10 Sekunden', icon: ArrowClockwise, color: 'text-red-400 bg-red-500/10 border-red-500/20', script_type: 'powershell', script_content: `Write-Output "Neustart wird in 10 Sekunden ausgeführt..."\nshutdown /r /t 10 /c "Geplanter Neustart via TSR Remote"` },
  { id: 'security', name: 'scripts.security', description: 'Firewall, Defender, Ports', icon: ShieldCheck, color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20', script_type: 'powershell', script_content: `Write-Output "=== Sicherheits-Check ==="\n$fw = Get-NetFirewallProfile | ForEach-Object { "$($_.Name): $($_.Enabled)" }\nWrite-Output "Firewall: $($fw -join ', ')"\n$defender = Get-MpComputerStatus -ErrorAction SilentlyContinue\nif ($defender) {\n  Write-Output "Defender: Echtzeit=$($defender.RealTimeProtectionEnabled)"\n} else { Write-Output "Defender: Nicht verfügbar" }` },
  { id: 'eventlog', name: 'scripts.eventlog', description: 'Letzte 20 Systemfehler', icon: FileText, color: 'text-pink-400 bg-pink-500/10 border-pink-500/20', script_type: 'powershell', script_content: `Write-Output "=== Letzte Fehler (System Event Log) ==="\nGet-EventLog -LogName System -EntryType Error -Newest 20 | ForEach-Object {\n  Write-Output "[$($_.TimeGenerated.ToString('dd.MM.yyyy HH:mm'))] $($_.Source): $($_.Message.Substring(0, [Math]::Min(120, $_.Message.Length)))..."\n}` },
];

const InfoRow = ({ label, value, mono = false, copyable = false }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    if (value) { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 2000); }
  };
  return (
    <div className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
      <span className="text-[#52525b] text-sm">{label}</span>
      <div className="flex items-center gap-2">
        <span className={`text-white ${mono ? 'font-mono text-sm' : ''}`}>{value || '-'}</span>
        {copyable && value && (
          <button onClick={handleCopy} className="p-1 text-[#52525b] hover:text-[#a1a1aa] transition-colors">
            {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
          </button>
        )}
      </div>
    </div>
  );
};

const SoftwareStatusCard = ({ label, id, onConnect, tag, t }) => (
  <div className="p-4 bg-[#0A0A0B] rounded border border-white/5">
    <div className="flex items-center gap-2 mb-3">
      <div className="w-8 h-8 rounded flex items-center justify-center bg-[#D50C2D]">
        <span className="text-white text-xs font-bold">{label}</span>
      </div>
      <span className="font-medium text-white">{label === 'TV' ? 'TeamViewer' : label === 'RD' ? 'RustDesk' : 'AnyDesk'}</span>
      {tag && <span className={`text-xs px-2 py-0.5 rounded ${tag.className}`}>{tag.text}</span>}
      <span className={`ml-auto led-indicator ${id ? 'led-green' : 'led-off'}`} />
    </div>
    <p className="font-mono text-sm text-[#a1a1aa] mb-3">{id || t('detail.notConfigured')}</p>
    <button onClick={onConnect} disabled={!id} data-testid={`connect-${label.toLowerCase()}-btn`}
      className="connect-btn hetzner-red w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed">
      {t('detail.connectVia')} {label === 'TV' ? 'TeamViewer' : label === 'RD' ? 'RustDesk' : 'AnyDesk'}
    </button>
  </div>
);

const StatusIcon = ({ status }) => {
  if (status === 'completed' || status === 'success') return <CheckCircle size={14} className="text-green-400" />;
  if (status === 'failed' || status === 'error') return <XCircle size={14} className="text-red-400" />;
  if (status === 'running') return <CircleNotch size={14} className="animate-spin text-blue-400" />;
  return <Clock size={14} className="text-yellow-400" />;
};

const AgentVersionRow = ({ version, agentType }) => {
  const [serverVersion, setServerVersion] = useState(null);
  useEffect(() => {
    fetch('/api/agent/v2/version').then(r => r.json()).then(d => setServerVersion(d.version)).catch(() => {});
  }, []);
  const isOutdated = serverVersion && version && version !== serverVersion;
  const isLegacy = !agentType || agentType !== 'node';
  return (
    <div className="flex items-center justify-between py-2 border-b border-white/[0.04] last:border-0">
      <span className="text-[#a1a1aa] text-sm">Agent Version</span>
      <div className="flex items-center gap-2">
        {version ? (
          <>
            <span className={`text-sm font-mono ${isOutdated ? 'text-orange-400' : 'text-green-400'}`}>{version}</span>
            {isOutdated && (
              <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-orange-500/10 text-orange-400 border border-orange-500/20">
                <WarningCircle size={10} /> Update verfuegbar: v{serverVersion}
              </span>
            )}
            {!isOutdated && serverVersion && (
              <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-green-500/10 text-green-400 border border-green-500/20">
                <CheckCircle size={10} /> Aktuell
              </span>
            )}
          </>
        ) : (
          <span className="text-[#52525b] text-sm">-</span>
        )}
        {isLegacy && (
          <span className="text-[10px] px-2 py-0.5 rounded bg-red-500/10 text-red-400 border border-red-500/20">
            Legacy - v2 empfohlen
          </span>
        )}
      </div>
    </div>
  );
};


export default function DeviceDetailPage() {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const { isTechnician, isAdmin } = useAuth();
  const { subscribe } = useWebSocket();
  const { t } = useLanguage();
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [customScriptOpen, setCustomScriptOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [runningTemplate, setRunningTemplate] = useState(null);
  const [deviceTasks, setDeviceTasks] = useState([]);
  const [expandedTask, setExpandedTask] = useState(null);
  const [customForm, setCustomForm] = useState({ name: '', script_content: '', script_type: 'powershell' });
  const [editForm, setEditForm] = useState({
    computer_name: '', teamviewer_id: '', rustdesk_id: '', anydesk_id: '',
    location_city: '', location_address: '', station_code: '',
  });
  const [hostnameDialogOpen, setHostnameDialogOpen] = useState(false);
  const [newHostname, setNewHostname] = useState('');
  const [changingHostname, setChangingHostname] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [togglingKiosk, setTogglingKiosk] = useState(false);

  const fetchDevice = async () => {
    try {
      const response = await devicesAPI.getById(deviceId);
      setDevice(response.data);
      setEditForm({
        computer_name: response.data.computer_name || '', teamviewer_id: response.data.teamviewer_id || '',
        rustdesk_id: response.data.rustdesk_id || '', anydesk_id: response.data.anydesk_id || '',
        location_city: response.data.location_city || '', location_address: response.data.location_address || '',
        station_code: response.data.station_code || '',
      });
    } catch (error) {
      toast.error(t('detail.notFound'));
      navigate('/devices');
    } finally { setLoading(false); }
  };

  const fetchDeviceTasks = async () => {
    try {
      const res = await api.get('/scripts');
      const tasks = res.data.tasks.filter(t =>
        t.target_device_ids && t.target_device_ids.includes(deviceId)
      );
      setDeviceTasks(tasks.slice(0, 10));
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchDevice(); fetchDeviceTasks(); }, [deviceId]);

  useEffect(() => {
    const unsubscribe = subscribe((message) => {
      if (message.type === 'device_status' && message.device_id === deviceId) {
        setDevice(prev => prev ? { ...prev, is_online: message.is_online, last_seen: message.last_seen, agent_type: message.agent_type || prev.agent_type, ...(message.tv_running !== undefined ? { tv_running: message.tv_running } : {}), ...(message.rd_running !== undefined ? { rd_running: message.rd_running } : {}), ...(message.ad_running !== undefined ? { ad_running: message.ad_running } : {}) } : null);
      }
    });
    return unsubscribe;
  }, [subscribe, deviceId]);

  useEffect(() => {
    const hasRunning = deviceTasks.some(t => t.status === 'pending' || t.status === 'running');
    if (!hasRunning) return;
    const interval = setInterval(fetchDeviceTasks, 5000);
    return () => clearInterval(interval);
  }, [deviceTasks]);

  const handleHostnameChange = async () => {
    if (!newHostname.trim() || newHostname.length > 15) {
      toast.error('Hostname muss 1-15 Zeichen lang sein');
      return;
    }
    setChangingHostname(true);
    try {
      await api.post(`/device/${deviceId}/set-hostname`, { hostname: newHostname.trim() });
      toast.success(`Hostname wird geaendert: ${device.computer_name} -> ${newHostname.trim()}. Geraet startet automatisch neu.`);
      setHostnameDialogOpen(false);
      fetchDevice();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Hostname-Aenderung fehlgeschlagen');
    } finally { setChangingHostname(false); }
  };

  const handleRestart = async () => {
    if (!confirm('Geraet wirklich neu starten?')) return;
    setRestarting(true);
    try {
      await api.post('/agent/restart/' + deviceId);
      toast.success('Neustart-Befehl gesendet (in 10 Sekunden)');
    } catch (e) {
      toast.error('Neustart fehlgeschlagen');
    } finally { setRestarting(false); }
  };

  const handleToggleKiosk = async () => {
    const isEnabled = device.kiosk_mode;
    const action = isEnabled ? 'deaktivieren' : 'aktivieren';
    if (!confirm('Kiosk-Modus ' + action + '?\n(Wird nach Neustart wirksam)')) return;
    setTogglingKiosk(true);
    try {
      await api.post('/agent/kiosk-mode/' + deviceId + '?enable=' + (!isEnabled));
      toast.success('Kiosk-Modus wird ' + (isEnabled ? 'deaktiviert' : 'aktiviert') + ' (nach Neustart wirksam)');
      setTimeout(fetchDevice, 1000);
    } catch (e) {
      toast.error('Kiosk-Umschaltung fehlgeschlagen');
    } finally { setTogglingKiosk(false); }
  };

  const handleConnect = (type) => {
    if (!device) return;
    const configs = {
      TV: { id: device.teamviewer_id, url: /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ? `https://start.teamviewer.com/${device.teamviewer_id}` : `teamviewer10://control?device=${device.teamviewer_id}`, name: 'TeamViewer' },
      RD: { id: device.rustdesk_id, url: `rustdesk://${device.rustdesk_id}`, name: 'RustDesk' },
      AD: { id: device.anydesk_id, url: `anydesk:${device.anydesk_id}`, mobile: true, name: 'AnyDesk' },
    };
    const cfg = configs[type];
    if (!cfg.id) { toast.error(`${cfg.name} ${t('connect.notAvailable')}`); return; }
    const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
    if (isMobile && cfg.mobile) {
      navigator.clipboard.writeText(String(cfg.id)).then(() => {
        toast.success(`${cfg.name} ${t('connect.idCopied')} ${cfg.id}`, { duration: 5000 });
      }).catch(() => { toast.info(`${cfg.name} ID: ${cfg.id}`, { duration: 5000 }); });
      return;
    }
    window.open(cfg.url, '_blank');
    toast.success(`${t('connect.connectingTo')} ${device.computer_name} via ${cfg.name}`);
  };

  const executeTemplate = async (tpl) => {
    setRunningTemplate(tpl.id);
    try {
      await api.post('/scripts', {
        name: `${t(tpl.name)} - ${device.computer_name}`,
        script_content: tpl.script_content,
        script_type: tpl.script_type,
        target_type: 'device',
        target_ids: [deviceId],
        run_as_admin: true,
        silent: true,
      });
      toast.success(`"${t(tpl.name)}" ${device.computer_name}`);
      fetchDeviceTasks();
    } catch (e) {
      toast.error(t('scripts.executeError'));
    } finally { setRunningTemplate(null); }
  };

  const handleCustomScript = async () => {
    if (!customForm.name.trim() || !customForm.script_content.trim()) {
      toast.error(t('scripts.nameAndScriptRequired'));
      return;
    }
    setSaving(true);
    try {
      await api.post('/scripts', {
        name: `${customForm.name} - ${device.computer_name}`,
        script_content: customForm.script_content,
        script_type: customForm.script_type,
        target_type: 'device',
        target_ids: [deviceId],
        run_as_admin: true,
        silent: true,
      });
      toast.success(t('scripts.scriptRunning'));
      setCustomScriptOpen(false);
      setCustomForm({ name: '', script_content: '', script_type: 'powershell' });
      fetchDeviceTasks();
    } catch (e) { toast.error(t('scripts.executeError')); }
    finally { setSaving(false); }
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await devicesAPI.update(deviceId, editForm);
      toast.success(t('detail.updated'));
      setEditOpen(false);
      fetchDevice();
    } catch (error) { toast.error(t('detail.updateError')); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await devicesAPI.delete(deviceId);
      toast.success(t('detail.deleted'));
      navigate('/devices');
    } catch (error) { toast.error(t('detail.deleteError')); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><CircleNotch size={32} className="animate-spin text-[#D50C2D]" /></div>;
  }
  if (!device) return null;

  return (
    <div data-testid="device-detail-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
        <Link to="/devices" className="p-2 text-[#a1a1aa] hover:text-white hover:bg-white/5 rounded transition-colors" data-testid="back-to-devices-btn">
          <ArrowLeft size={20} />
        </Link>
        <div className="flex-1">
          <h1 className="font-heading text-2xl font-bold text-white tracking-tight">{device.computer_name}</h1>
          <div className="flex items-center gap-3 mt-1">
            <span className={`status-badge ${device.is_online ? 'online' : 'offline'}`}>
              <span className={`status-dot ${device.is_online ? 'online status-pulse-online' : 'offline'}`} />
              {device.is_online ? t('common.online') : t('common.offline')}
            </span>
            <span className="tenant-badge">{device.tenant_code}</span>
            <div className="software-leds ml-2">
              <div className="software-led-item" title={device.teamviewer_id || 'Kein TV'}><span className={`led-indicator ${device.teamviewer_id ? 'led-green' : 'led-off'}`} /><span>TV</span>{device.teamviewer_id && <span className="text-[10px] font-mono text-[#52525b] ml-0.5">{device.teamviewer_id}</span>}</div>
              <div className="software-led-item" title={device.rustdesk_id || 'Kein RD'}><span className={`led-indicator ${device.rustdesk_id ? 'led-green' : 'led-off'}`} /><span>RD</span>{device.rustdesk_id && <span className="text-[10px] font-mono text-[#52525b] ml-0.5">{device.rustdesk_id}</span>}</div>
              <div className="software-led-item" title={device.anydesk_id || 'Kein AD'}><span className={`led-indicator ${device.anydesk_id ? 'led-green' : 'led-off'}`} /><span>AD</span>{device.anydesk_id && <span className="text-[10px] font-mono text-[#52525b] ml-0.5">{device.anydesk_id}</span>}</div>
            </div>
          </div>
        </div>
        {isTechnician && (
          <Button onClick={() => setEditOpen(true)} variant="outline" data-testid="edit-device-btn" className="border-white/10 hover:bg-white/5">
            <PencilSimple size={16} /><span>{t('detail.editDevice')}</span>
          </Button>
        )}
        {isAdmin && (
          <Button onClick={handleToggleKiosk} disabled={togglingKiosk || !device.is_online} size="sm"
            className={device.kiosk_mode ? "bg-green-600/20 hover:bg-green-600/30 text-green-400 border border-green-500/30" : "bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-500/30"}
            data-testid="kiosk-toggle-btn">
            <span>{togglingKiosk ? '...' : device.kiosk_mode ? 'Kiosk EIN' : 'Kiosk AUS'}</span>
          </Button>
        )}
        {isAdmin && (
          <Button onClick={handleRestart} disabled={restarting || !device.is_online} size="sm"
            className="bg-orange-600/20 hover:bg-orange-600/30 text-orange-400 border border-orange-500/30" data-testid="restart-device-btn">
            <ArrowClockwise size={16} className={restarting ? 'animate-spin' : ''} /><span>Neustart</span>
          </Button>
        )}
        {isAdmin && (
          <Button onClick={() => setDeleteOpen(true)} variant="destructive" data-testid="delete-device-btn">
            <Trash size={16} /><span>{t('detail.deleteDevice')}</span>
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-[#141416] border border-white/10 rounded p-6">
            <h2 className="font-heading font-semibold text-white mb-4">{t('detail.remoteConnection')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <SoftwareStatusCard label="TV" id={device.teamviewer_id} onConnect={() => handleConnect('TV')} tag={{ text: t('detail.primary'), className: 'bg-[#D50C2D]/20 text-[#D50C2D]' }} t={t} />
              <SoftwareStatusCard label="RD" id={device.rustdesk_id} onConnect={() => handleConnect('RD')} tag={{ text: t('detail.fallback'), className: 'bg-white/10 text-[#a1a1aa]' }} t={t} />
              <SoftwareStatusCard label="AD" id={device.anydesk_id} onConnect={() => handleConnect('AD')} tag={{ text: t('detail.alternative'), className: 'bg-orange-500/20 text-orange-400' }} t={t} />
            </div>
            <Link to={"/filemanager/" + device.id} className="mt-4 flex items-center gap-2 px-4 py-3 bg-[#0A0A0B] rounded border border-white/5 hover:border-[#D50C2D]/50 transition-all group" data-testid="open-filemanager-btn">
              <FolderOpen size={20} className="text-[#D50C2D]" />
              <span className="text-white group-hover:text-[#D50C2D] transition-colors">Dateimanager</span>
              <span className="ml-auto text-[#52525b] text-xs">Dateien durchsuchen</span>
            </Link>
          </div>

          <div className="bg-[#141416] border border-white/10 rounded p-6" data-testid="device-scripts-section">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Terminal size={18} className="text-[#D50C2D]" />
                <h2 className="font-heading font-semibold text-white">{t('detail.executeScripts')}</h2>
              </div>
              {isAdmin && (
                <Button size="sm" onClick={() => { setCustomScriptOpen(true); setCustomForm({ name: '', script_content: '', script_type: 'powershell' }); }}
                  className="bg-white/5 hover:bg-white/10 text-white border border-white/10" data-testid="custom-script-btn">
                  <Terminal size={14} /> {t('detail.customScript')}
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {scriptTemplates.map(tpl => (
                <button key={tpl.id} onClick={() => executeTemplate(tpl)} disabled={runningTemplate === tpl.id}
                  className="flex items-center gap-3 p-3 bg-[#0A0A0B] rounded border border-white/5 hover:border-white/15 transition-colors text-left group disabled:opacity-60"
                  data-testid={`run-template-${tpl.id}`}>
                  <div className={`p-1.5 rounded border shrink-0 ${tpl.color}`}>
                    {runningTemplate === tpl.id ? <CircleNotch size={16} className="animate-spin" /> : <tpl.icon size={16} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-white text-sm font-medium block truncate">{t(tpl.name)}</span>
                    <span className="text-[#52525b] text-xs block truncate">{tpl.description}</span>
                  </div>
                  <Play size={14} className="text-[#52525b] group-hover:text-[#D50C2D] transition-colors shrink-0" />
                </button>
              ))}
            </div>
          </div>

          {deviceTasks.length > 0 && (
            <div className="bg-[#141416] border border-white/10 rounded p-6" data-testid="device-tasks-section">
              <div className="flex items-center gap-2 mb-4">
                <Clock size={18} className="text-[#D50C2D]" />
                <h2 className="font-heading font-semibold text-white">{t('detail.recentResults')}</h2>
              </div>
              <div className="space-y-2">
                {deviceTasks.map(task => {
                  const myResult = task.results?.find(r => r.device_id === deviceId);
                  const taskStatus = myResult?.status || task.status;
                  return (
                    <div key={task.id} className="bg-[#0A0A0B] rounded border border-white/5 overflow-hidden">
                      <div className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-white/[0.02] transition-colors"
                        onClick={() => setExpandedTask(expandedTask === task.id ? null : task.id)}>
                        {expandedTask === task.id ? <CaretDown size={12} className="text-[#52525b]" /> : <CaretRight size={12} className="text-[#52525b]" />}
                        <StatusIcon status={taskStatus} />
                        <span className="text-white text-sm font-medium flex-1 truncate">{task.name}</span>
                        <span className="text-[#52525b] text-xs">{new Date(task.created_at).toLocaleString('de-DE')}</span>
                      </div>
                      {expandedTask === task.id && myResult && (
                        <div className="px-3 pb-3 border-t border-white/5">
                          {myResult.output && <pre className="text-xs text-green-400/80 font-mono mt-2 whitespace-pre-wrap break-all">{myResult.output}</pre>}
                          {myResult.error && <pre className="text-xs text-red-400/80 font-mono mt-2 whitespace-pre-wrap break-all">{myResult.error}</pre>}
                          {myResult.exit_code !== null && myResult.exit_code !== undefined && <span className="text-[#52525b] text-xs font-mono block mt-1">Exit Code: {myResult.exit_code}</span>}
                          {!myResult.output && !myResult.error && <p className="text-[#52525b] text-xs mt-2">{t('detail.noResult')}</p>}
                        </div>
                      )}
                      {expandedTask === task.id && !myResult && task.status === 'pending' && (
                        <div className="px-3 pb-3 border-t border-white/5">
                          <p className="text-[#52525b] text-xs mt-2">{t('detail.waitingResult')}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="bg-[#141416] border border-white/10 rounded p-6">
            <div className="flex items-center gap-2 mb-4">
              <Info size={18} className="text-[#D50C2D]" />
              <h2 className="font-heading font-semibold text-white">{t('detail.deviceInfo')}</h2>
            </div>
            <InfoRow label={t('detail.deviceId')} value={device.id} mono copyable />
            <div className="flex items-center justify-between py-3 border-b border-white/5">
              <span className="text-[#52525b] text-sm">Hostname</span>
              <div className="flex items-center gap-2">
                <span className="text-white font-mono text-sm">{device.computer_name || '-'}</span>
                {device.system_info?.hostname && device.system_info.hostname !== device.computer_name && (
                  <span className="text-[#52525b] text-xs">(Agent: {device.system_info.hostname})</span>
                )}
                {isAdmin && (
                  <button onClick={() => { setNewHostname(device.computer_name || ''); setHostnameDialogOpen(true); }}
                    className="p-1 text-[#52525b] hover:text-[#D50C2D] transition-colors" data-testid="edit-hostname-btn">
                    <PencilSimple size={14} />
                  </button>
                )}
              </div>
            </div>
            <InfoRow label="TeamViewer ID" value={device.teamviewer_id} mono copyable />
            <InfoRow label="RustDesk ID" value={device.rustdesk_id} mono copyable />
            <InfoRow label="AnyDesk ID" value={device.anydesk_id} mono copyable />
            <InfoRow label={t('detail.ipAddress')} value={device.ip_address} mono copyable />
            <InfoRow label={t('detail.os')} value={device.os_info} />
          </div>

          {/* ===== HARDWARE & SYSTEM INFO ===== */}
          {device.system_info && (
            <div className="bg-[#141416] border border-white/10 rounded p-6" data-testid="system-info-section">
              <div className="flex items-center gap-2 mb-4">
                <Desktop size={18} className="text-[#D50C2D]" />
                <h2 className="font-heading font-semibold text-white">Hardware & System</h2>
                {device.system_info.collected_at && (
                  <span className="ml-auto text-[10px] text-[#52525b]">
                    Erfasst: {new Date(device.system_info.collected_at).toLocaleString('de-DE')}
                  </span>
                )}
              </div>

              {/* System Overview */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-[#0A0A0B] rounded border border-white/5 p-4">
                  <h3 className="text-xs font-semibold text-[#D50C2D] uppercase tracking-wider mb-2">System</h3>
                  <InfoRow label="Hersteller" value={device.system_info.manufacturer} />
                  <InfoRow label="Modell" value={device.system_info.model} />
                  <InfoRow label="Domain" value={device.system_info.domain} />
                  <InfoRow label="Benutzer" value={device.system_info.current_user} />
                  <InfoRow label="Letzter Boot" value={device.system_info.last_boot} />
                  <InfoRow label="Uptime" value={device.system_info.uptime_hours ? `${Math.round(device.system_info.uptime_hours)}h` : null} />
                </div>
                <div className="bg-[#0A0A0B] rounded border border-white/5 p-4">
                  <h3 className="text-xs font-semibold text-[#D50C2D] uppercase tracking-wider mb-2">BIOS / Mainboard</h3>
                  <InfoRow label="BIOS Hersteller" value={device.system_info.bios_manufacturer} />
                  <InfoRow label="BIOS Version" value={device.system_info.bios_version} />
                  <InfoRow label="BIOS Seriennr." value={device.system_info.bios_serial} mono copyable />
                  <InfoRow label="Mainboard" value={device.system_info.mainboard_product} />
                  <InfoRow label="MB Hersteller" value={device.system_info.mainboard_manufacturer} />
                  <InfoRow label="MB Seriennr." value={device.system_info.mainboard_serial} mono copyable />
                </div>
              </div>

              {/* CPU & RAM */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-[#0A0A0B] rounded border border-white/5 p-4">
                  <h3 className="text-xs font-semibold text-[#D50C2D] uppercase tracking-wider mb-2">Prozessor</h3>
                  <InfoRow label="CPU" value={device.system_info.cpu_name} />
                  <InfoRow label="Kerne" value={device.system_info.cpu_cores} />
                  <InfoRow label="Threads" value={device.system_info.cpu_threads} />
                  <InfoRow label="Takt" value={device.system_info.cpu_speed_ghz ? `${device.system_info.cpu_speed_ghz} GHz` : null} />
                </div>
                <div className="bg-[#0A0A0B] rounded border border-white/5 p-4">
                  <h3 className="text-xs font-semibold text-[#D50C2D] uppercase tracking-wider mb-2">Arbeitsspeicher</h3>
                  <InfoRow label="Gesamt" value={device.system_info.total_ram_gb ? `${device.system_info.total_ram_gb} GB` : null} />
                  {device.system_info.ram_modules && device.system_info.ram_modules.map((m, i) => (
                    <InfoRow key={i} label={`Modul ${i+1}`} value={m ? `${m.capacity_gb || '?'} GB ${m.type || ''} ${m.speed_mhz ? m.speed_mhz + ' MHz' : ''}`.trim() : null} />
                  ))}
                </div>
              </div>

              {/* GPUs */}
              {device.system_info.gpus && device.system_info.gpus.length > 0 && (
                <div className="bg-[#0A0A0B] rounded border border-white/5 p-4 mb-4">
                  <h3 className="text-xs font-semibold text-[#D50C2D] uppercase tracking-wider mb-2">Grafikkarten</h3>
                  {device.system_info.gpus.map((g, i) => (
                    <InfoRow key={i} label={`GPU ${i+1}`} value={g.name ? `${g.name}${g.ram_gb ? ` (${g.ram_gb} GB)` : ''}` : null} />
                  ))}
                </div>
              )}

              {/* Disks & Volumes */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {device.system_info.physical_disks && device.system_info.physical_disks.length > 0 && (
                  <div className="bg-[#0A0A0B] rounded border border-white/5 p-4">
                    <h3 className="text-xs font-semibold text-[#D50C2D] uppercase tracking-wider mb-2">Physische Datentraeger</h3>
                    {device.system_info.physical_disks.map((d, i) => (
                      <div key={i} className="py-2 border-b border-white/5 last:border-0">
                        <span className="text-white text-sm">{d.model || `Disk ${i}`}</span>
                        <div className="flex gap-4 mt-1">
                          {d.size_gb && <span className="text-[#52525b] text-xs">{d.size_gb} GB</span>}
                          {d.media_type && <span className="text-[#52525b] text-xs">{d.media_type}</span>}
                          {d.serial && <span className="text-[#52525b] text-xs font-mono">{d.serial}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {device.system_info.volumes && device.system_info.volumes.length > 0 && (
                  <div className="bg-[#0A0A0B] rounded border border-white/5 p-4">
                    <h3 className="text-xs font-semibold text-[#D50C2D] uppercase tracking-wider mb-2">Volumes</h3>
                    {device.system_info.volumes.map((v, i) => (
                      <div key={i} className="py-2 border-b border-white/5 last:border-0">
                        <div className="flex items-center justify-between">
                          <span className="text-white text-sm font-mono">{v.drive || v.letter || `Vol ${i}`}</span>
                          <span className="text-[#52525b] text-xs">{v.size_gb ? `${v.free_gb || 0}/${v.size_gb} GB frei` : ''}</span>
                        </div>
                        {v.size_gb && v.free_gb && (
                          <div className="w-full h-1.5 bg-[#1A1A1C] rounded mt-1.5">
                            <div className="h-full rounded" style={{
                              width: `${Math.min(100, Math.round(((v.size_gb - v.free_gb) / v.size_gb) * 100))}%`,
                              backgroundColor: ((v.size_gb - v.free_gb) / v.size_gb) > 0.9 ? '#ef4444' : ((v.size_gb - v.free_gb) / v.size_gb) > 0.7 ? '#f97316' : '#22c55e'
                            }} />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Network */}
              {device.system_info.network_adapters && device.system_info.network_adapters.length > 0 && (
                <div className="bg-[#0A0A0B] rounded border border-white/5 p-4 mb-4">
                  <h3 className="text-xs font-semibold text-[#D50C2D] uppercase tracking-wider mb-2">Netzwerk</h3>
                  {device.system_info.network_adapters.map((n, i) => (
                    <div key={i} className="py-2 border-b border-white/5 last:border-0">
                      <span className="text-white text-sm">{n.name || `Adapter ${i}`}</span>
                      <div className="flex gap-4 mt-1 flex-wrap">
                        {n.ip && <span className="text-[#52525b] text-xs font-mono">{n.ip}</span>}
                        {n.mac && <span className="text-[#52525b] text-xs font-mono">{n.mac}</span>}
                        {n.speed && <span className="text-[#52525b] text-xs">{n.speed}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Monitors */}
              {device.system_info.monitors && device.system_info.monitors.length > 0 && (
                <div className="bg-[#0A0A0B] rounded border border-white/5 p-4 mb-4">
                  <h3 className="text-xs font-semibold text-[#D50C2D] uppercase tracking-wider mb-2">Monitore</h3>
                  {device.system_info.monitors.map((m, i) => (
                    <InfoRow key={i} label={`Monitor ${i+1}`} value={`${m.manufacturer || '?'} ${m.name || ''}${m.serial ? ` (S/N: ${m.serial})` : ''}`} />
                  ))}
                </div>
              )}

              {/* Scanner Devices */}
              {device.system_info.scanner_devices && device.system_info.scanner_devices.length > 0 && (
                <div className="bg-[#0A0A0B] rounded border border-emerald-500/20 p-4 mb-4" data-testid="scanner-devices-section">
                  <h3 className="text-xs font-semibold text-emerald-400 uppercase tracking-wider mb-3">
                    Scanner ({device.system_info.scanner_devices.length})
                  </h3>
                  {device.system_info.scanner_devices.map((s, i) => (
                    <div key={i} className="py-3 border-b border-white/5 last:border-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-0.5 rounded text-xs font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">{s.type}</span>
                        <span className="text-white text-sm font-medium">{s.name}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                        <div className="flex justify-between"><span className="text-[#52525b]">Hersteller</span><span className="text-white">{s.manufacturer || '-'}</span></div>
                        <div className="flex justify-between"><span className="text-[#52525b]">USB SN</span><span className="text-white font-mono">{s.serial || '-'}</span></div>
                        {s.software_version && (
                          <div className="flex justify-between"><span className="text-[#52525b]">Release</span><span className="text-emerald-400 font-mono">v.{s.software_version}</span></div>
                        )}
                        {s.dr_sdk && (
                          <div className="flex justify-between"><span className="text-[#52525b]">DR SDK</span><span className="text-emerald-400 font-mono">v.{s.dr_sdk}</span></div>
                        )}
                        {s.camera_driver && (
                          <div className="flex justify-between"><span className="text-[#52525b]">Camera Driver</span><span className="text-white font-mono">v.{s.camera_driver}</span></div>
                        )}
                        {s.database_version && (
                          <div className="flex justify-between"><span className="text-[#52525b]">Database</span><span className="text-white font-mono">v.{s.database_version}</span></div>
                        )}
                        {s.sw_serial_number && (
                          <div className="flex justify-between"><span className="text-[#52525b]">Serial Number</span><span className="text-amber-400 font-mono">{s.sw_serial_number}</span></div>
                        )}
                        {s.label_serial_number && (
                          <div className="flex justify-between"><span className="text-[#52525b]">Label Serial</span><span className="text-amber-400 font-mono">{s.label_serial_number}</span></div>
                        )}
                        {s.updates_valid_until && (
                          <div className="flex justify-between col-span-2"><span className="text-[#52525b]">Updates gueltig bis</span><span className="text-yellow-400 font-mono">{s.updates_valid_until}</span></div>
                        )}
                      </div>
                    </div>
                  ))}
                  {device.system_info.regula_info && (
                    <div className="mt-3 pt-3 border-t border-emerald-500/10">
                      <h4 className="text-xs text-emerald-400/70 mb-2">Regula Software-Komponenten</h4>
                      <div className="space-y-1">
                        {(device.system_info.regula_info.components || []).map((c, i) => (
                          <div key={i} className="flex justify-between text-xs">
                            <span className="text-[#52525b]">{c.name}</span>
                            <span className="text-white font-mono">{c.version}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* USB Devices */}
              {device.system_info.usb_devices && device.system_info.usb_devices.length > 0 && (
                <div className="bg-[#0A0A0B] rounded border border-white/5 p-4 mb-4">
                  <h3 className="text-xs font-semibold text-[#D50C2D] uppercase tracking-wider mb-2">USB-Geraete ({device.system_info.usb_devices.length})</h3>
                  {device.system_info.usb_devices.map((u, i) => (
                    <div key={i} className="py-1.5 border-b border-white/5 last:border-0 flex items-center justify-between">
                      <span className="text-white text-sm">{u.name || `USB ${i}`}</span>
                      {u.serial && <span className="text-[#52525b] text-xs font-mono">{u.serial}</span>}
                    </div>
                  ))}
                </div>
              )}

              {/* Battery */}
              {device.system_info.battery && device.system_info.battery.has_battery && (
                <div className="bg-[#0A0A0B] rounded border border-white/5 p-4 mb-4">
                  <h3 className="text-xs font-semibold text-[#D50C2D] uppercase tracking-wider mb-2">Akku</h3>
                  <InfoRow label="Ladung" value={device.system_info.battery.charge_percent ? `${device.system_info.battery.charge_percent}%` : null} />
                  <InfoRow label="Status" value={device.system_info.battery.status} />
                </div>
              )}

              {/* Installed Software */}
              {device.system_info.installed_software && device.system_info.installed_software.length > 0 && (
                <details className="bg-[#0A0A0B] rounded border border-white/5 p-4">
                  <summary className="text-xs font-semibold text-[#D50C2D] uppercase tracking-wider cursor-pointer select-none">
                    Installierte Software ({device.system_info.installed_software.length})
                  </summary>
                  <div className="mt-2 max-h-64 overflow-y-auto">
                    {device.system_info.installed_software.map((s, i) => (
                      <div key={i} className="py-1 border-b border-white/5 last:border-0 flex items-center justify-between">
                        <span className="text-white text-sm truncate mr-2">{s.name || '?'}</span>
                        <span className="text-[#52525b] text-xs shrink-0">{s.version || ''}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="bg-[#141416] border border-white/10 rounded p-6">
            <div className="flex items-center gap-2 mb-4"><MapPin size={18} className="text-[#D50C2D]" /><h2 className="font-heading font-semibold text-white">{t('detail.locationTitle')}</h2></div>
            <InfoRow label={t('detail.city')} value={device.location_city} />
            <InfoRow label={t('detail.address')} value={device.location_address} />
            <InfoRow label={t('detail.stationCode')} value={device.station_code} mono />
          </div>
          <div className="bg-[#141416] border border-white/10 rounded p-6">
            <div className="flex items-center gap-2 mb-4"><Clock size={18} className="text-[#D50C2D]" /><h2 className="font-heading font-semibold text-white">{t('detail.statusTitle')}</h2></div>
            <InfoRow label={t('devices.tenant')} value={device.tenant_name} />
            <InfoRow label={t('common.status')} value={device.is_online ? t('common.online') : t('common.offline')} />
            <InfoRow label="Agent" value={device.agent_type === 'node' ? 'v2 (Node.js / WebSocket)' : 'v1 (PowerShell / REST)'} />
            <AgentVersionRow version={device.agent_version} agentType={device.agent_type} />
            <InfoRow label={t('detail.lastSeen')} value={device.last_seen ? new Date(device.last_seen).toLocaleString('de-DE') : t('detail.never')} />
            <InfoRow label={t('common.created')} value={new Date(device.created_at).toLocaleDateString('de-DE')} />
          </div>
        </div>
      </div>

      {/* Hostname Change Dialog */}
      <Dialog open={hostnameDialogOpen} onOpenChange={setHostnameDialogOpen}>
        <DialogContent className="bg-[#141416] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Hostname aendern</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[#a1a1aa]">Aktueller Hostname</Label>
              <p className="text-white font-mono mt-1">{device?.computer_name || '-'}</p>
            </div>
            <div>
              <Label htmlFor="new-hostname" className="text-[#a1a1aa]">Neuer Hostname</Label>
              <Input id="new-hostname" value={newHostname} onChange={e => setNewHostname(e.target.value.toUpperCase())}
                maxLength={15} placeholder="z.B. TSRHQ2-01"
                className="bg-[#0A0A0B] border-white/10 text-white mt-1 font-mono" data-testid="new-hostname-input" />
              <p className="text-[#52525b] text-xs mt-1">Max. 15 Zeichen. Das Geraet wird automatisch neu gestartet.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHostnameDialogOpen(false)} className="border-white/10 text-white">Abbrechen</Button>
            <Button onClick={handleHostnameChange} disabled={changingHostname || !newHostname.trim()}
              className="bg-[#D50C2D] hover:bg-[#D50C2D]/80 text-white" data-testid="confirm-hostname-btn">
              {changingHostname ? <CircleNotch size={16} className="animate-spin" /> : 'Hostname aendern & Neustart'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={customScriptOpen} onOpenChange={setCustomScriptOpen}>
        <DialogContent className="bg-[#141416] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{t('detail.customScript')} - {device.computer_name}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#a1a1aa]">{t('common.name')}</Label>
                <Input value={customForm.name} onChange={e => setCustomForm(p => ({...p, name: e.target.value}))} className="bg-[#0A0A0B] border-white/10 mt-1" placeholder={t('scripts.scriptName')} />
              </div>
              <div>
                <Label className="text-[#a1a1aa]">{t('common.type')}</Label>
                <Select value={customForm.script_type} onValueChange={v => setCustomForm(p => ({...p, script_type: v}))}>
                  <SelectTrigger className="bg-[#0A0A0B] border-white/10 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141416] border-white/10">
                    <SelectItem value="powershell">PowerShell</SelectItem>
                    <SelectItem value="cmd">CMD</SelectItem>
                    <SelectItem value="batch">Batch</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-[#a1a1aa]">Skript</Label>
              <textarea value={customForm.script_content} onChange={e => setCustomForm(p => ({...p, script_content: e.target.value}))}
                className="w-full mt-1 p-3 bg-[#0A0A0B] border border-white/10 rounded text-sm text-white font-mono resize-y min-h-[120px] focus:outline-none focus:border-[#D50C2D]"
                placeholder="Get-Process | Select-Object Name, CPU" data-testid="custom-script-content" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomScriptOpen(false)} className="border-white/10">{t('common.cancel')}</Button>
            <Button onClick={handleCustomScript} disabled={saving} className="bg-[#D50C2D] hover:bg-[#f21d41]">
              {saving ? <CircleNotch size={16} className="animate-spin" /> : <><Play size={16} /> {t('common.execute')}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[#141416] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-heading">{t('detail.editDevice')}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label className="text-[#a1a1aa]">{t('devices.computerName')}</Label><Input value={editForm.computer_name} onChange={(e) => setEditForm(prev => ({ ...prev, computer_name: e.target.value }))} className="bg-[#0A0A0B] border-white/10 mt-1" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div><Label className="text-[#a1a1aa]">TeamViewer ID</Label><Input value={editForm.teamviewer_id} onChange={(e) => setEditForm(prev => ({ ...prev, teamviewer_id: e.target.value }))} className="bg-[#0A0A0B] border-white/10 mt-1 font-mono" /></div>
              <div><Label className="text-[#a1a1aa]">RustDesk ID</Label><Input value={editForm.rustdesk_id} onChange={(e) => setEditForm(prev => ({ ...prev, rustdesk_id: e.target.value }))} className="bg-[#0A0A0B] border-white/10 mt-1 font-mono" /></div>
              <div><Label className="text-[#a1a1aa]">AnyDesk ID</Label><Input value={editForm.anydesk_id} onChange={(e) => setEditForm(prev => ({ ...prev, anydesk_id: e.target.value }))} className="bg-[#0A0A0B] border-white/10 mt-1 font-mono" /></div>
            </div>
            <div><Label className="text-[#a1a1aa]">{t('detail.city')}</Label><Input value={editForm.location_city} onChange={(e) => setEditForm(prev => ({ ...prev, location_city: e.target.value }))} className="bg-[#0A0A0B] border-white/10 mt-1" /></div>
            <div><Label className="text-[#a1a1aa]">{t('detail.address')}</Label><Input value={editForm.location_address} onChange={(e) => setEditForm(prev => ({ ...prev, location_address: e.target.value }))} className="bg-[#0A0A0B] border-white/10 mt-1" /></div>
            <div><Label className="text-[#a1a1aa]">{t('detail.stationCode')}</Label><Input value={editForm.station_code} onChange={(e) => setEditForm(prev => ({ ...prev, station_code: e.target.value }))} className="bg-[#0A0A0B] border-white/10 mt-1 font-mono" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="border-white/10">{t('common.cancel')}</Button>
            <Button onClick={handleSaveEdit} disabled={saving} className="bg-[#D50C2D] hover:bg-[#f21d41]">{saving ? <CircleNotch size={16} className="animate-spin" /> : t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="bg-[#141416] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-heading">{t('detail.deleteDevice')}</DialogTitle></DialogHeader>
          <p className="text-[#a1a1aa]">{t('detail.deleteDevice')}: <span className="text-white font-medium">{device.computer_name}</span> {t('detail.deleteConfirm')}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} className="border-white/10">{t('common.cancel')}</Button>
            <Button onClick={handleDelete} disabled={saving} variant="destructive">{saving ? <CircleNotch size={16} className="animate-spin" /> : t('common.delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
